/* ═══════════════════════════════════════════════════════════════
   Timesheets rebuild (Tranche 2) — clean-sheet replacement for the
   Timesheets portion of the old TimeTrackingPage.tsx god-file.

   Kill list executed here: per-keystroke saves, hand-rolled
   <table>/ProjectPicker/badges, document.getElementById date read,
   the silent forecast auto-seed, hardcoded 40h/8h red alarm color.

   Mountable standalone. Accepts the exact same deep-link shape the
   app shell already sends this surface (view:'time-expense') so a
   future shell swap is a prop-for-prop drop-in.
   ═══════════════════════════════════════════════════════════════ */

import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Icon } from '../../components/ui/Icon';
import { rem } from '../../lib/rem';

import { Input } from '../../components/ui/input';
import { SearchableSelect } from '../../components/SearchableSelect';
import EmptyState from '../../components/EmptyState';
import { StatusBadge } from '../../components/StatusBadge';
import { RejectReasonDialog } from './RejectReasonDialog';
import { ApprovalQueuePanel } from './ApprovalQueuePanel';
import { TimesheetSidebar } from './TimesheetSidebar';
import { TimesheetGrid, computeWeekTotals } from './TimesheetGrid';
import { SummaryCards } from './SummaryCards';
import { useTimesheetData } from './useTimesheetData';
import { exportTimesheetCSV } from './csv';
import { hasPermission } from '../../../shared/permissions';
import { formatShort, addDays, mondayOf, mondayOfTodayISO, todayISO, yearOf } from './dateUtils';
import type { AuthUser } from '../../services/authService';
import type { TimeExpenseDeepLink } from './types';

export interface TimesheetsPageProps {
  toast: (msg: string, isError?: boolean) => void;
  authUser?: AuthUser;
  deepLink?: TimeExpenseDeepLink | null;
  onDeepLinkConsumed?: () => void;
}

export default function TimesheetsPage({
  toast,
  authUser,
  deepLink,
  onDeepLinkConsumed,
}: TimesheetsPageProps) {
  const data = useTimesheetData({ authUser, deepLink, onDeepLinkConsumed, toast });
  const [rejectOpen, setRejectOpen] = useState(false);
  const canExport = hasPermission(authUser?.permissions, 'timesheets.export');

  // Deep-link "project" — light scaffold-only enhancement: surface the
  // project row so a dashboard "drill into this project's hours" click
  // lands somewhere visible, without inventing scroll/highlight machinery.
  // Captured up front (deepLink is one-shot-consumed by the data hook)
  // and applied only after the week finishes loading, so it can never
  // be clobbered by loadWeek's own projectRows rebuild.
  const [pendingProjectId, setPendingProjectId] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (deepLink?.projectId) setPendingProjectId(deepLink.projectId);
  }, [deepLink?.projectId]);
  useEffect(() => {
    if (pendingProjectId && data.selectedMemberId && !data.loadingWeek) {
      data.ensureProjectRowForProject(pendingProjectId);
      setPendingProjectId(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingProjectId, data.selectedMemberId, data.loadingWeek]);

  if (data.error) {
    return (
      <div style={{ padding: 'var(--space-5)', color: 'var(--dg)' }} role="alert">
        {data.error}
      </div>
    );
  }

  const todayStr = todayISO();
  const memberName = data.members.find((m) => m.id === data.selectedMemberId)?.name || '';
  const canUnsubmitNow = data.isApprover || data.canSelfUnsubmit(data.timesheet);
  const weekTotals = computeWeekTotals(
    data.entries,
    data.forecasts,
    data.vacationRequests,
    data.firmHolidays,
    data.vacationCategories,
    data.overheadCategories,
    data.projectRows,
    data.weekDates
  );

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      <TimesheetSidebar
        memberName={memberName}
        currentWeekStart={data.weekStart}
        allTimesheets={data.allTimesheets}
        onSelectWeek={data.setWeekStart}
        onDelete={data.deleteTimesheet}
        onAddByDate={data.createTimesheetForDate}
      />

      <div style={{ flex: 1, overflow: 'auto', padding: 'var(--space-5)' }}>
        {data.isApprover && (
          <ApprovalQueuePanel
            queue={data.queue}
            members={data.members}
            onReview={(memberId, weekStart) => {
              data.setSelectedMemberId(memberId);
              data.setWeekStart(weekStart);
            }}
            onApprove={(id) => data.handleApprove(id)}
            onReject={(id, reason) => data.handleReject(reason, id)}
          />
        )}

        {/* Header row: week nav + member switcher + status + actions */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-3)',
            marginBottom: 'var(--space-4)',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
            <Button
              variant="outline"
              size="icon"
              aria-label="Previous week"
              onClick={() => data.setWeekStart(addDays(data.weekStart, -7))}
            >
              <Icon icon={ChevronLeft} em={1.2308} />
            </Button>
            <Input
              type="date"
              aria-label="Jump to week"
              value={data.weekStart}
              onChange={(e) => e.target.value && data.setWeekStart(mondayOf(e.target.value))}
              style={{ width: rem(150) }}
            />
            <Button
              variant="outline"
              size="icon"
              aria-label="Next week"
              onClick={() => data.setWeekStart(addDays(data.weekStart, 7))}
            >
              <Icon icon={ChevronRight} em={1.2308} />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => data.setWeekStart(mondayOfTodayISO())}
            >
              Today
            </Button>
          </div>

          <span style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700 }}>
            {formatShort(data.weekStart)} — {formatShort(addDays(data.weekStart, 6))},{' '}
            {yearOf(data.weekStart)}
          </span>

          {data.timesheet && <StatusBadge status={data.timesheet.status} />}

          {data.canViewOthers && (
            <div style={{ minWidth: rem(220) }}>
              <SearchableSelect
                value={data.selectedMemberId}
                options={data.members.map((m) => ({ value: m.id, label: m.name }))}
                onChange={data.setSelectedMemberId}
                placeholder="Viewing…"
                searchPlaceholder="Search people…"
              />
            </div>
          )}

          {data.timesheet && (
            <div
              style={{
                marginLeft: 'auto',
                display: 'flex',
                gap: 'var(--space-2)',
                flexWrap: 'wrap',
              }}
            >
              {canExport && (
                <Button
                  onClick={() =>
                    exportTimesheetCSV({
                      weekStart: data.weekStart,
                      weekDates: data.weekDates,
                      overheadNames: data.overheadCategories.map((c) => c.name),
                      projectRows: data.projectRows,
                      projects: data.projects,
                      activityCodes: data.activityCodes,
                      entries: data.entries,
                    })
                  }
                  variant="outline"
                  size="sm"
                >
                  Export
                </Button>
              )}
              <Button onClick={() => window.print()} variant="outline" size="sm">
                Print
              </Button>
              {data.timesheet.status === 'draft' && (
                <Button onClick={data.handleSubmit} variant="default" size="sm">
                  Submit
                </Button>
              )}
              {data.timesheet.status === 'submitted' && canUnsubmitNow && (
                <Button onClick={data.handleUnsubmit} variant="outline" size="sm">
                  Unsubmit
                </Button>
              )}
              {data.timesheet.status === 'submitted' && data.isApprover && (
                <>
                  <Button onClick={() => data.handleApprove()} variant="default" size="sm">
                    Approve
                  </Button>
                  <Button onClick={() => setRejectOpen(true)} variant="destructive" size="sm">
                    Reject
                  </Button>
                </>
              )}
              {data.timesheet.status === 'approved' && data.isApprover && (
                <Button onClick={data.handleUnapprove} variant="outline" size="sm">
                  Unapprove
                </Button>
              )}
              {data.timesheet.status === 'approved' && data.isApprover && (
                <Button onClick={data.handleLock} variant="destructive" size="sm">
                  Lock
                </Button>
              )}
              {data.timesheet.status === 'locked' && data.canUnlock && (
                <Button onClick={data.handleUnlock} variant="outline" size="sm">
                  Unlock
                </Button>
              )}
            </div>
          )}
        </div>

        {!data.selectedMemberId ? (
          <EmptyState title="Loading…" subtitle="Fetching your team and project data." />
        ) : (
          <>
            <TimesheetGrid
              weekDates={data.weekDates}
              todayStr={todayStr}
              entries={data.entries}
              forecasts={data.forecasts}
              vacationRequests={data.vacationRequests}
              firmHolidays={data.firmHolidays}
              vacationCategories={data.vacationCategories}
              overheadCategories={data.overheadCategories}
              projectRows={data.projectRows}
              projects={data.projects}
              activityCodes={data.activityCodes}
              allPhases={data.allPhases}
              isLocked={data.isLocked}
              onSaveProjectHours={data.saveProjectHours}
              onSaveOverheadHours={data.saveOverheadHours}
              onAddRow={data.addProjectRow}
              onRemoveRow={data.removeProjectRow}
              onUpdateRow={data.updateProjectRow}
              onAcceptRow={data.acceptRow}
              onAcceptWeek={data.acceptWeek}
            />
            <SummaryCards
              weekTotal={weekTotals.weekTotal}
              projectWeekTotal={weekTotals.projectWeekTotal}
              overheadWeekTotal={weekTotals.overheadWeekTotal}
            />
          </>
        )}
      </div>

      <RejectReasonDialog
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        memberName={memberName}
        onConfirm={(reason) => {
          data.handleReject(reason);
          setRejectOpen(false);
        }}
      />
    </div>
  );
}
